<template>
    <div>
        <md-progress-spinner v-if="isLoading"
            md-mode="indeterminate"></md-progress-spinner>
        <md-card v-else
            class="md-layout-item md-size-100 md-small-size-100">
            <md-card-content>
                <md-field md-clearable
                    :class="{'md-invalid': $v.user.first_name.$dirty && $v.user.first_name.$invalid}">
                    <label>{{ $t('users.first-name') }}</label>
                    <md-input v-model="user.first_name"
                        @input="$v.user.first_name.$touch()"></md-input>
                    <span class="md-error"
                        v-show="$v.user.first_name.$dirty && $v.user.first_name.$invalid">{{ $tc('validations.required-field', 1) }}</span>
                </md-field>

                <md-field md-clearable
                    :class="{'md-invalid': $v.user.last_name.$dirty && $v.user.last_name.$invalid}">
                    <label>{{ $t('users.last-name') }}</label>
                    <md-input v-model="user.last_name"
                        @input="$v.user.last_name.$touch()"></md-input>
                    <span class="md-error"
                        v-show="$v.user.last_name.$dirty && $v.user.last_name.$invalid">{{ $tc('validations.required-field', 1) }}</span>
                </md-field>

                <div class="md-layout md-gutter">
                    <div class="md-layout-item"
                        :class="{'md-size-80': this.changePasswordDisabled}">
                        <md-field :md-toggle-password="true"
                            :class="{'md-invalid': (!this.changePasswordDisabled || !this.isEditMode) && ($v.user.password.$dirty && $v.user.password.$invalid)}">
                            <label>{{ $t('users.password') }}</label>
                            <md-input v-model="user.password"
                                @input="$v.user.password.$touch()"
                                type="password"
                                :disabled="changePasswordDisabled"></md-input>
                            <span class="md-error"
                                v-if="!this.isEditMode"
                                v-show="$v.user.password.$dirty && $v.user.password.$required && $v.user.password.$minLength">{{ $t('validations.min-length',{num: $v.user.password.$params.minLength.min}) }}. {{ $tc('validations.required-field', 1) }}</span>
                            <span class="md-error"
                                v-if="!this.changePasswordDisabled"
                                v-show="$v.user.password.$dirty && $v.user.password.$required && $v.user.password.$minLength">{{ $t('validations.min-length',{num: $v.user.password.$params.minLength.min}) }}. {{ $tc('validations.required-field', 1) }}</span>
                        </md-field>
                    </div>
                    <div class="md-layout-item md-size-20"
                        v-if="this.changePasswordDisabled">
                        <md-button @click="toggleChangePassword"
                            class="md-dense md-primary">
                            {{ $t('users.change-password') }}
                        </md-button>
                    </div>
                </div>

                <md-field md-clearable
                    :class="{'md-invalid': $v.user.phone.$dirty && $v.user.phone.$invalid}">
                    <label>{{ $t('users.phone') }}</label>
                    <md-input v-model="user.phone"
                        @input="$v.user.phone.$touch()"></md-input>
                    <span class="md-error"
                        v-show="$v.user.phone.$dirty && $v.user.phone.$invalid">{{ $tc('validations.required-field', 1) }}</span>
                </md-field>

                <md-field md-clearable
                    :class="{'md-invalid': $v.user.email.$dirty && $v.user.email.$invalid}">
                    <label>{{ $t('users.email') }}</label>
                    <md-input v-model="user.email"
                        @input="$v.user.email.$touch()"></md-input>
                    <span class="md-error"
                        v-show="$v.user.email.$dirty && $v.user.email.$invalid">{{ $t('validations.email') }}. {{ $tc('validations.required-field', 1) }}</span>
                </md-field>

                <md-field :class="{'md-invalid': $v.organization.organization_name.$dirty && $v.organization.organization_name.$invalid}"
                    v-if="!this.isAddOrganizationMode">
                    <label>{{ $t('users.organization') }}</label>
                    <md-input disabled
                        v-model="organization.organization_name"
                        @input="$v.organization.organization_name.$touch()"></md-input>
                    <span class="md-error"
                        v-show="$v.organization.organization_name.$dirty && $v.organization.organization_name.$invalid">{{ $tc('validations.required-field', 1) }}</span>
                </md-field>

                <md-field v-if="!this.isAddOrganizationMode">
                    <md-select v-model="user.users_roles_id"
                        :placeholder=" $t('users.role') ">
                        <md-option v-for="role in userRoles"
                            :key="role.users_roles_permitted_id"
                            :value="role.users_roles_permitted_id">
                            {{ role.role_name }}
                        </md-option>
                    </md-select>
                </md-field>

                <div class="action-bar">
                    <md-button class="md-raised md-primary"
                        @click="previous"
                        v-if="this.isAddOrganizationMode">
                        {{ $t('actions.previous') }}
                    </md-button>
                    <md-button class="md-raised"
                        @click="close">
                        {{ $t('actions.cancel') }}
                    </md-button>
                    <md-button class="md-raised md-primary"
                        @click="saveUser"
                        :disabled="$v.$invalid">
                        {{ this.isAddOrganizationMode ? $t('actions.next') : $t('actions.confirm') }}
                    </md-button>
                </div>
            </md-card-content>
        </md-card>
    </div>
</template>

<script>
import _ from 'lodash'
import usersService from '@/components/users/services/users'
import userRolesService from '@/components/users/services/usersRoles'
import organizationsService from '@/components/organizations/services/organizations'
import { required, minLength, email, requiredIf } from 'vuelidate/lib/validators'

const loadingKeys = {
    creatingItem: 'creating-item',
    fetchingItem: 'fetching-item'
}

export default {
    name: 'Maintenance',

    data () {
        return {
            user: {
                first_name: '',
                last_name: '',
                password: '',
                phone: '',
                email: '',
                users_roles_id: null
            },

            organization: {
                city: '',
                email: '',
                logo_picture_id: null,
                logo_picture_url: '',
                nipc: '',
                organization_id: null,
                organization_name: '',
                organization_picture_id: null,
                organization_picture_url: '',
                phone: null,
                preferred_delivery_hour: '',
                state: null,
                street: null,
                zip_code: null
            },

            userRoles: [],

            isViewMode: false,
            isAddMode: false,
            isEditMode: false,
            isAddOrganizationMode: false,

            loadings: [],

            changePasswordRequested: false
        }
    },

    validations: {
        user: {
            first_name: {
                required
            },
            last_name: {
                required
            },
            password: {
                required: requiredIf(function () {
                    return !this.isEditMode || !this.changePasswordDisabled
                }),
                minLength: minLength(8)
            },
            phone: {},
            email: {
                required,
                email
            },
            users_roles_id: {
                required: requiredIf(function () {
                    return !this.isAddOrganizationMode
                })
            }
        },
        organization: {
            organization_name: {
                required: requiredIf(function () {
                    return !this.isAddOrganizationMode
                })
            }
        }
    },

    computed: {
        isLoading () {
            return this.isFetching || this.isCreatingusers
        },

        isCreatingusers () {
            return !!this.loadings.filter(
                loading => loading.key === loadingKeys.creatingItem
            ).length
        },

        isFetching () {
            return !!this.loadings.filter(
                loading => loading.key === loadingKeys.fetchingItem
            ).length
        },

        changePasswordDisabled () {
            return this.isEditMode && !this.changePasswordRequested
        }
    },

    methods: {
        setMode (mode) {
            switch (mode) {
                case 'view-user':
                    this.isViewMode = true
                    break
                case 'add-user':
                    this.isAddMode = true
                    break
                case 'edit-user':
                    this.isEditMode = true
                    break
                case 'add-organization':
                    this.isAddOrganizationMode = true
                    break
            }
        },

        toggleChangePassword () {
            this.changePasswordRequested = !this.changePasswordRequested
        },

        previous () {
            // emit event to parent
            this.$emit('previous')
        },

        async saveUser () {
            _.fill(this.loadings, loadingKeys.creatingItem)

            let self = this
            let params = {
                user_id: self.user.user_id,
                first_name: self.user.first_name,
                last_name: self.user.last_name,
                password: self.user.password,
                phone: self.user.phone,
                email: self.user.email,
                active: this.isAddMode || this.isAddOrganizationMode ? 1 : self.user.active,
                organization_id: self.organization.organization_id,
                users_roles_id: self.user.users_roles_id,
                password_updated: self.changePasswordRequested
            }
            // eslint-disable-next-line
            let response

            if (this.isAddMode) {
                response = await usersService.create(params)
            } else if (this.isAddOrganizationMode) {
                // emit event to parent
                this.$emit('newuser', params)
            } else {
                response = await usersService.update(params)
            }

            _.pull(this.loadings, loadingKeys.creatingItem)

            if (!this.isAddOrganizationMode) {
                this.$router.go(-1)
            }
        },

        async fetchUserRoles () {
            _.fill(this.loadings, loadingKeys.fetchingItem)

            let self = this
            let params = {
                'users_roles_id': this.$store.getters.user.roleId
            }

            let response = await userRolesService.getPermitted(params)

            self.userRoles = _.cloneDeep(response.data)

            // checks if is ADMIN organization and let Admin role available in select options
            let organizationId = this.$store.getters.user.organizationId
            let userRole = this.$store.getters.user.role
            if (userRole === 'ADMIN') {
                if (organizationId !== parseInt(self.$route.params.id)) {
                    // remove admin from user roles array
                    self.userRoles = self.userRoles.filter(function (item) {
                        return item.role_name !== 'ADMIN'
                    })
                }
            }

            _.pull(this.loadings, loadingKeys.fetchingItem)
        },

        async fetchUser () {
            _.fill(this.loadings, loadingKeys.fetchingItem)

            let self = this
            let params = {
                filter: {
                    user_id: self.$route.params.user_id
                }
            }

            let response = await usersService.getAll(params)
            if (this.isEditMode) {
                this.password = response.data.results[0].password
            }
            self.user = _.cloneDeep(response.data.results[0])

            _.pull(this.loadings, loadingKeys.fetchingItem)
        },

        async fetchOrganization () {
            _.fill(this.loadings, loadingKeys.fetchingItem)

            let self = this
            let params = {
                filter: {
                    organization_id: self.$route.params.id
                }
            }

            let response = await organizationsService.retrieve(params)
            self.organization = _.cloneDeep(response.data.results[0])

            _.pull(this.loadings, loadingKeys.fetchingItem)
        },

        close () {
            this.$router.go(-1)
        }
    },
    created () {
        this.setMode(this.$route.name)

        this.fetchUserRoles()
        if (!this.isAddOrganizationMode) {
            this.fetchOrganization()
        }
        if (!(this.isAddMode || this.isAddOrganizationMode)) {
            this.fetchUser()
        }
    }
}
</script>
